import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDateFilteredLeads } from "@/hooks/useDateFilteredData";
import { useUsers } from "@/hooks";
import { LeadsStats } from "../components/LeadsStats";
import { useLeadStats } from "@/hooks/useGlobalStats";
import { LeadsFilterSection } from "../components/LeadsFilterSection";
import { LeadTableView } from "../components/LeadTableView";
import { LeadKanban } from "../components/LeadKanban";
import { PipelineTabs } from "../components/PipelineTabs";
import { LeadsModals } from "../components/LeadsModals";
import { LeadsTabContent } from "../components/LeadsTabContent";
import { InlineLoader as Loader, Pagination } from "@/components/ui";
import { LEAD_STAGES, getCumulativeStatuses } from "@/constants/globalConstants";
import { useAuthStore } from "@/store/authStore";
import { useLeadFilters } from "../hooks/useLeadFilters";
import { useLeadActivityStats } from "../hooks/useLeadActivityStats";
import { useLeadsHandlers } from "../hooks/useLeadsHandlers";

export default function LeadsScreen({ activeService = "Verve" }) {
  const { user } = useAuthStore();
  const navigate = useNavigate();

  // 1. Filter Management
  const {
    search, setSearch, debouncedSearch,
    view, setView,
    page, setPage,
    sortBy, setSortBy,
    filters, setFilters,
    showAdvancedFilters, setShowAdvancedFilters,
    activeTab, setSearchParams
  } = useLeadFilters();

  // getCumulativeStatuses is imported from globalConstants

  const queryParams = useMemo(() => ({
    search: debouncedSearch,
    page,
    limit: 10,
    status: filters.status === 'new' ? 'new' : getCumulativeStatuses(filters.status),
    activityStatus: filters.activityStatus !== 'all' ? filters.activityStatus : undefined,
    source: filters.source !== 'all' ? filters.source : undefined,
    city: filters.location !== 'all' ? filters.location : undefined,
    assignedTo: filters.assignedTo !== 'all' ? filters.assignedTo : undefined,
    date: filters.date !== 'all' ? filters.date : undefined,
    fromDate: filters.fromDate,
    toDate: filters.toDate,
    sort: sortBy
  }), [debouncedSearch, page, filters, sortBy]);

  const activityParams = useMemo(() => ({
    search: debouncedSearch,
    status: filters.status === 'new' ? 'new' : getCumulativeStatuses(filters.status),
    activityStatus: filters.activityStatus !== 'all' ? filters.activityStatus : undefined,
    source: filters.source !== 'all' ? filters.source : undefined,
    location: filters.location !== 'all' ? filters.location : undefined,
    assignedTo: filters.assignedTo !== 'all' ? filters.assignedTo : undefined,
    date: filters.date,
    fromDate: filters.fromDate,
    toDate: filters.toDate
  }), [debouncedSearch, filters]);

  const { data: leadsData, isLoading: leadsLoading, refetch } = useDateFilteredLeads(queryParams, filters);
  const { data: activityStats } = useLeadActivityStats(activityParams);
  const { teamMembers: teamData } = useUsers({ limit: 1000, includeOwner: true });

  const rawLeads = Array.isArray(leadsData) ? leadsData : (leadsData?.leads || leadsData?.projects || []);
  const leads = rawLeads.map(l => ({
    ...l,
    isLocked: false
  }));
  const totalLeads = Array.isArray(leadsData) ? leadsData.length : (leadsData?.total || 0);

  const filterOptions = useMemo(() => {
    if (!rawLeads.length) return { locations: [], sources: [], assignees: [] };
    const locations = [...new Set(rawLeads.map(l => l.city).filter(Boolean))].sort();
    const sources = [...new Set(rawLeads.map(l => l.source).filter(Boolean))].sort();
    const assigneesMap = new Map();
    rawLeads.forEach(l => {
      const assigned = l.assignedTo?._id ? l.assignedTo : (typeof l.assignedTo === 'string' ? { _id: l.assignedTo, name: 'User' } : null);
      if (assigned?._id) assigneesMap.set(assigned._id, assigned.name);
    });
    return { locations, sources, assignees: Array.from(assigneesMap, ([id, name]) => ({ id, name })) };
  }, [rawLeads]);

  // Derived statistics using global hook
  const stats = useLeadStats(leadsData?.stats || {});

  const team = teamData || [];

  // Filter team members by role and location for assignment
  const salesExecs = useMemo(() => {
    return team.filter(u => {
      const uRole = (u.role || "").toLowerCase().trim().replace(/[-_\s]+/g, "-");
      return ["sales-exec", "sale-exec", "sales", "sales-executive"].includes(uRole);
    });
  }, [team]);

  const projectManagers = useMemo(() => {
    return team.filter(u => {
      const uRole = (u.role || "").toLowerCase().trim().replace(/[-_\s]+/g, "-");
      return ["project-manager", "pm"].includes(uRole);
    });
  }, [team]);

  // 2. State for Modals
  const [selectedLead, setSelectedLead] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [confirmState, setConfirmState] = useState({ isOpen: false, title: "", message: "", onConfirm: null, loading: false });
  const [isStatsExpanded, setIsStatsExpanded] = useState(false);
  
  // Appointment Specific Modals
  const [isAppointmentFormOpen, setIsAppointmentFormOpen] = useState(false);



  const activeLead = useMemo(() => {
    if (!selectedLead) return null;
    return leads.find(l => l._id === selectedLead._id) || selectedLead;
  }, [selectedLead, leads]);

  // 3. Handlers
  const {
    handleLeadSubmit,
    handleUpdateLead,
    toggleLeadStatus,
    handleAppointmentSubmit,
    handleExport,
    handleReset
  } = useLeadsHandlers({
    selectedLead,
    setSelectedLead,
    setIsFormOpen,
    setPendingStatus,
    setIsAppointmentFormOpen,
    refetch,
    isExporting,
    setIsExporting,
    queryParams,
    setFilters,
  });

  // Removed full page loader to prevent page flash, passing isLoading to child views instead.
  const isDesignerUser = user?.role === "designer";
  const isPMUser = user?.role === "project-manager";
  
  const displayLeadStages = isPMUser
    ? LEAD_STAGES.filter(stage => ['site_visit', 'initial_quotation'].includes(stage.key))
    : LEAD_STAGES;

  return (
    <div className="flex flex-col gap-6 w-full max-w-full pb-12 overflow-x-hidden">
      {/* Global layout for all tabs since none are unmigrated anymore! */}
      {activeTab === 'all' && (
        <div className="relative overflow-hidden w-full h-32 bg-white border border-slate-200 rounded-xl flex items-center justify-between">
          <div className="pl-8 z-20">
            <h1 className="font-serif font-medium text-3xl text-slate-900 tracking-wide">
              All Leads
            </h1>
            <p className="text-sm font-body text-slate-400 mt-2 font-medium">Verve Kitchens Lead Management</p>
          </div>
          <div className="w-[50%] h-full relative overflow-hidden flex-shrink-0">
            <div className="absolute inset-0 bg-gradient-to-r from-white via-white/70 to-transparent z-10 w-48" />
            <img
              src="/kitchens/1.jpg"
              alt="Lead Management"
              className="absolute top-0 right-0 w-full h-full object-cover object-center"
            />
          </div>
        </div>
      )}

      {activeTab === 'all' && (
        <LeadsStats
          stats={stats}
          isExpanded={isStatsExpanded}
          onToggle={() => setIsStatsExpanded(!isStatsExpanded)}
        />
      )}
    {/* updated by: Halimunnisa 09-06-2026
 PipelineTabs is now always rendered since all tabs are migrated, with the 'All' tab showing comprehensive stats and the other tabs providing focused views. The 'hideAll' prop is used to conditionally hide the 'All' tab for PM users, as per original code. */}
      <PipelineTabs 
        currentTab={activeTab} 
        onTabChange={(tab) => {
          setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (tab === 'all') next.delete('tab');
            else next.set('tab', tab);
            return next;
          });
          setFilters(prev => ({ ...prev, status: tab }));
        }}
        stages={displayLeadStages}
        hideAll={isPMUser}
      />

          {activeTab !== 'appointment_scheduled' && activeTab !== 'site_visit' && activeTab !== 'initial_quotation' && activeTab !== 'customer_acceptance' && activeTab !== 'cust_acceptance' && (
            <div className="flex flex-col rounded-2xl overflow-hidden bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] min-w-0">
            <LeadsFilterSection
              search={search} setSearch={setSearch}
              filters={filters} setFilters={setFilters}
              sortBy={sortBy} setSortBy={setSortBy}
              showAdvancedFilters={showAdvancedFilters} setShowAdvancedFilters={setShowAdvancedFilters}
              view={view} setView={setView}
              onAddClick={() => { setSelectedLead(null); setIsFormOpen(true); }}
              onExport={handleExport}
              isExporting={isExporting}
              stages={LEAD_STAGES}
              filterOptions={filterOptions}
              activityStats={activityStats}
              onReset={handleReset}
            />
            </div>
          )}

          <LeadsTabContent
            view={view}
            filters={filters}
            leads={leads}
            leadsLoading={leadsLoading}
            user={user}
            teamMembers={team}
            salesExecs={salesExecs}
            projectManagers={projectManagers}
            setSelectedLead={setSelectedLead}
            setIsDetailOpen={setIsDetailOpen}
            setIsFormOpen={setIsFormOpen}
            setIsAppointmentFormOpen={setIsAppointmentFormOpen}
            setConfirmState={setConfirmState}
            refetch={refetch}
            toggleLeadStatus={toggleLeadStatus}
            handleUpdateLead={handleUpdateLead}
            handleExport={handleExport}
            isExporting={isExporting}
          />

          <Pagination
            page={page}
            totalPages={Math.ceil(totalLeads / 10)}
            onPageChange={setPage}
          />

          <LeadsModals
            isFormOpen={isFormOpen}
            pendingStatus={pendingStatus}
            isDetailOpen={isDetailOpen}
            confirmState={confirmState}
            isAppointmentFormOpen={isAppointmentFormOpen}
            
            setIsFormOpen={setIsFormOpen}
            setPendingStatus={setPendingStatus}
            setIsDetailOpen={setIsDetailOpen}
            setConfirmState={setConfirmState}
            setIsAppointmentFormOpen={setIsAppointmentFormOpen}
            setSelectedLead={setSelectedLead}
            
            activeLead={activeLead}
            team={team}
            user={user}
            projectManagers={projectManagers}
            leadStages={LEAD_STAGES}
            
            handleLeadSubmit={handleLeadSubmit}
            refetch={refetch}
            toggleLeadStatus={toggleLeadStatus}
            handleUpdateLead={handleUpdateLead}
            handleAppointmentSubmit={handleAppointmentSubmit}
          />
    </div>
  );
}
